import React, { useMemo } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "react-i18next";
import classes from "./Modal.module.scss";
import RoundButton from "../Buttons/RoundButton";
import Spinner from "../../components/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";
import AlertSucessDialog from "../AlertSuccessDialog/AlertSuccessDialog";
import CircularProgress from "@mui/material/CircularProgress";
import { useGroupsManager } from "./hooks/useGroupsManager";
import { Box } from "@mui/material";

const ModalGroups = (props: any) => {
  const { textButton, buttonDesabled = false, companyId, hasNewGroups } = props;
  const { t } = useTranslation();

  // Estado do modal de confirmação
  const [confirmationDialog, setConfirmationDialog] = React.useState<any>({
    open: false,
    title: "",
    message: "",
    confirmText: "",
    showConfirmButton: false,
    onConfirm: undefined,
  });

  // Estado do modal de sucesso
  const [alertDialog, setAlertDialog] = React.useState<any>({
    open: false,
    message: "",
    icon: null,
  });

  // Fecha e limpa o modal de confirmação
  const closeConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      title: "",
      message: "",
      confirmText: "",
      showConfirmButton: false,
      onConfirm: undefined,
    });
  };

  // Fecha e limpa o alerta
  const closeAlertDialog = () => {
    setAlertDialog({
      open: false,
      message: "",
      icon: null,
    });
  };

  const {
    isLoading,
    isLoadingNewGroup,
    isLoadingInactivateGroups,
    getGroups,
    sendNewGroup,
    confirmDeleteGroup,
    open,
    setOpen,
    newGroupName,
    setNewGroupName,
    handleClickOpen,
    activeGroups,
  } = useGroupsManager({
    hasNewGroups,
    setAlertDialog,
    closeConfirmationDialog,
    companyId,
  });

  // Fecha o modal principal
  const handleClose = () => {
    setOpen(false);
    props.setHasUserChangedGroup((value: boolean) => !value);
  };

  // Remove um grupo
  const inactivateGrous = (
    groupId: any,
    groupName: any,
    companyUid: any,
    quantityUsers: any,
  ) => {
    // Impede exclusão se houver usuários vinculados
    if (quantityUsers > 0) {
      setOpen(false); //fecha modal dos grupos
      //abre modal de aviso
      setConfirmationDialog({
        open: true,
        title: t("global.unable_to_delete"),
        message: t("global.this_group_still_has_users"),
        confirmText: "",
        showConfirmButton: false,
        onConfirm: undefined,
      });
      return;
    }

    //dados do grupo selecionado
    const selectedGroup = {
      groupId,
      groupName,
      companyUid,
    };

    //Abre modal de confirmação de exclusão
    setConfirmationDialog({
      open: true,
      title: t("global.delete_group"),
      message: t("global.are_you_sure_you_want_to_delete_this_group"),
      confirmText: "Confirmar Exclusão",
      onConfirm: () => confirmDeleteGroup(selectedGroup),
    });

    setOpen(false); //fecha modal dos grupos
  };

  // Atualiza o nome do novo grupo
  const handleNewGroup = (e: any) => {
    setNewGroupName(e.target.value);
  };

  const hasActiveGroups = activeGroups.length > 0;

  const contentForm = useMemo(() => {
    return (
      <DialogContentText id="alert-dialog-description">
        {isLoading ? (
          <Spinner />
        ) : hasActiveGroups ? (
          <Box
            sx={{ maxHeight: "400px", minHeight: "200px", overflowY: "auto" }}
          >
            {activeGroups?.map((group: any) => (
              <div key={group.id} className={classes.Groups}>
                <>{`${group.name}`}</>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={isLoadingInactivateGroups}
                  onClick={() =>
                    inactivateGrous(
                      group.id,
                      group.name,
                      companyId,
                      group.quantity_users,
                    )
                  }
                >
                  {t("global.del_group")}
                </Button>
              </div>
            ))}
          </Box>
        ) : (
          <div>{t("global.no_groups")}</div>
        )}
        <DialogTitle
          id="alert-dialog-title"
          className={classes.TextDialogPrimary}
        >
          {t("global.add_group")}
        </DialogTitle>
        <form style={{ display: "flex", gap: "50px" }}>
          <label>{t("global.group_name")}</label>
          <input
            autoFocus
            type="text"
            id="fname"
            name="fname"
            value={newGroupName}
            onChange={(e) => handleNewGroup(e)}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={sendNewGroup}
            disabled={isLoadingNewGroup}
            sx={{
              color: "#007085",
              borderColor: "#007085",
              "&:hover": {
                borderColor: "#005c6d",
                fontWeight: 500,
                backgroundColor: "rgba(0, 113, 133, 0.158)",
              },
            }}
          >
            {isLoadingNewGroup ? (
              <CircularProgress sx={{ color: "#007085" }} size={20} />
            ) : (
              t("global.to_add")
            )}
          </Button>
        </form>
      </DialogContentText>
    );
  }, [
    isLoading,
    hasActiveGroups,
    activeGroups,
    isLoadingInactivateGroups,
    newGroupName,
    isLoadingNewGroup,
  ]);

  return (
    <>
      <RoundButton
        disabled={buttonDesabled}
        title={textButton}
        text={textButton}
        onClick={handleClickOpen}
        small
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="alert-dialog-title"
          className={classes.TextDialogPrimary}
        >
          {t("global.manage_groups")}
          <FontAwesomeIcon
            style={{ marginLeft: "60%", cursor: "pointer" }}
            icon={faRedo}
            onClick={() => {
              getGroups();
            }}
          />
        </DialogTitle>
        <DialogContent>{contentForm}</DialogContent>
        <DialogActions>
          <>
            <Button
              onClick={handleClose}
              sx={{
                color: "#007085",
                "&:hover": {
                  borderColor: "#005c6d",
                  fontWeight: 500,
                  backgroundColor: "rgba(0, 112, 133, 0.04)",
                },
              }}
              autoFocus
            >
              {t("global.close")}
            </Button>
          </>
        </DialogActions>
      </Dialog>
      <AlertSucessDialog
        open={alertDialog.open}
        message={alertDialog.message}
        onClose={closeAlertDialog}
        icon={alertDialog.icon}
      />
      <ConfirmationDialog
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        showConfirmButton={confirmationDialog.showConfirmButton}
        onClose={() => {
          setOpen(true);
          closeConfirmationDialog();
        }}
        onConfirm={confirmationDialog.onConfirm}
        disabled={isLoadingInactivateGroups}
      />
    </>
  );
};

export default ModalGroups;
